import {
  AvailableCn5gFunction,
  CN5G_FUNCTION_CONFIG,
  DuplexMode,
  FrequencyRange,
  FronthaulType,
  LicenseDto,
  LicenseStatus,
  ModalService,
  SimpleMessageService,
  Tenant,
  TenantId,
  UPF_COMPONENTS_CONFIG,
  UpfComponents,
  WebAppCommonModule,
} from "@allbesmart/web-app-common";
import {
  UserRole,
  WebAppKeycloakAuthModule,
} from "@allbesmart/web-app-keycloak-auth";
import { Component, OnDestroy } from "@angular/core";
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import Keycloak from "keycloak-js";
import { Subscription, first, map, of, switchMap, tap } from "rxjs";
import { UpdateDeploymentDomainModalComponent } from "src/app/components/update-deployment-domain-modal/update-deployment-domain-modal.component";
import { ActionCommand } from "src/app/enums/action-command";
import { ActionType } from "src/app/enums/action-type";
import { AvailableRoutes } from "src/app/enums/available-routes";
import { LicenseActionDto } from "src/app/models/action/action-dto";
import { DeploymentFlagsDto } from "src/app/models/tenant/tenant-flags-dto";
import { ActionsService } from "src/app/services/actions.service";
import { UpdateTenantDto } from "../../models/tenant/update-tenant-dto";
import { TenantManagementService } from "../../services/tenant-management.service";
import {
  DuplexModeFormGroup,
  FrequencyRangeFormGroup,
  FronthaulTypeFormGroup,
  UpdateDeploymentFormGroup,
  cn5gConfigurationValidator,
} from "./update-deployment-page-form";

const UUID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

@Component({
  selector: "oaibox-update-deployment-page",
  templateUrl: "./update-deployment-page.component.html",
  styleUrls: ["./update-deployment-page.component.css"],
  imports: [WebAppCommonModule, ReactiveFormsModule, WebAppKeycloakAuthModule],
})
export class UpdateDeploymentPageComponent implements OnDestroy {
  protected isRoot?: boolean;
  protected isSales?: boolean;

  protected readonly AVAILABLE_LICENSE_STATUS = Object.values(LicenseStatus);

  protected updateDeploymentForm: UpdateDeploymentFormGroup;

  protected tenant?: Tenant;
  protected tenantLogoUrl?: string;
  protected addTenantLogoFile?: File;
  protected removeTenantLogoFile: boolean = false;

  private updateDeploymentSubscription?: Subscription;
  private tenantSubscription?: Subscription;
  private refreshTenantSubscription?: Subscription;
  private tenantFlagsSubscription?: Subscription;

  private domainAutoAssignmentEnabled?: boolean;

  protected ongoingRequest: boolean = false;

  constructor(
    activatedRoute: ActivatedRoute,
    keycloak: Keycloak,
    private readonly modalService: ModalService,
    private readonly simpleMessageService: SimpleMessageService,
    private readonly tenantManagementService: TenantManagementService,
    private readonly router: Router,
    private readonly actionsService: ActionsService
  ) {
    this.isRoot = keycloak.hasRealmRole(UserRole.ROOT);
    this.isSales = keycloak.hasRealmRole(UserRole.SALES);

    const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

    this.updateDeploymentForm = new FormGroup({
      name: new FormControl<string | null>(null, [Validators.required]),
      description: new FormControl<string | null>(null, [Validators.required]),
      domainAutoAssignment: new FormControl<boolean>(false),
      licenseConfiguration: new FormGroup({
        configurationIsShown: new FormControl<boolean | null>(false),
        status: new FormControl<LicenseStatus | null>(
          LicenseStatus.ACTIVE,
          Validators.required
        ),
        startDate: new FormControl<string | null>(today, Validators.required),
        endDate: new FormControl<string | null>(today),
      }),
      offlineConfiguration: new FormGroup({
        enabled: new FormControl<boolean>(false),
      }),
      fronthaulTypes: new FormArray(
        Object.values(FronthaulType)
          .filter((fronthaul) => fronthaul !== FronthaulType.NONE)
          .map(
            (fronthaul) =>
              new FormGroup({
                checked: new FormControl<boolean>(
                  fronthaul === FronthaulType.SPLIT_8
                ),
                type: new FormControl<FronthaulType>(fronthaul),
              }) as FronthaulTypeFormGroup
          )
      ),

      frequencyRanges: new FormArray(
        Object.values(FrequencyRange).map(
          (frequencyRange) =>
            new FormGroup({
              checked: new FormControl<boolean>(
                frequencyRange === FrequencyRange.FR1
              ),
              type: new FormControl<FrequencyRange>(frequencyRange),
            }) as FrequencyRangeFormGroup
        )
      ),

      duplexModes: new FormArray(
        Object.values(DuplexMode).map(
          (duplexMode) =>
            new FormGroup({
              checked: new FormControl<boolean>(duplexMode === DuplexMode.TDD),
              type: new FormControl<DuplexMode>(duplexMode),
            }) as DuplexModeFormGroup
        )
      ),

      education: new FormControl<boolean>(false),
      kubernetes: new FormControl<boolean>(false),
      advancedMode: new FormControl<boolean>(false),

      // Features
      flexRic: new FormControl<boolean>(true),
      ueManagement: new FormGroup({
        enabled: new FormControl<boolean>(false),
        oaiUeMachineIds: new FormArray<FormControl<string | null>>([]),
      }),
      networkSlicing: new FormControl<boolean>(false),

      // Premium Features
      mobility: new FormControl<boolean>(false),
      qosium: new FormControl<boolean>(false),
      ris: new FormControl<boolean>(false),
      multiRan: new FormControl<boolean>(false),
      redCapConfiguration: new FormGroup({
        redCap: new FormControl<boolean>(false),
        halfDuplexRedCapAllowedR17: new FormControl<number>(0),
        cellBarredRedCap1RxR17: new FormControl<number>(1),
        cellBarredRedCap2RxR17: new FormControl<number>(1),
        intraFreqReselectionRedCapR17: new FormControl<number>(0),
        t300: new FormControl<number>(400),
        t301: new FormControl<number>(400),
        t310: new FormControl<number>(2000),
        t311: new FormControl<number>(3000),
        n310: new FormControl<number>(20),
        n311: new FormControl<number>(1),
        t319: new FormControl<number>(400),
      }),

      // Use Cases Library
      digitalTwin: new FormControl<boolean>(false),
      isac: new FormControl<boolean>(false),
      radarDetection: new FormControl<boolean>(false),
      visionAidedCommunications: new FormControl<boolean>(false),

      cn5gConfiguration: new FormGroup(
        {
          cn5gRemote: new FormControl<boolean>(false),
          cn5gIp: new FormControl<string | null>(null),
          cn5gInterfaceName: new FormControl<string | null>(null),
          cn5gNextHop: new FormControl<string | null>(null),
          cn5gAmfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.AMF].defaultIp ?? null
          ),
          cn5gUpfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UPF].defaultIp ?? null
          ),
          cn5gSmfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.SMF].defaultIp ?? null
          ),
          cn5gAusfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.AUSF].defaultIp ?? null
          ),
          cn5gUdmIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UDM].defaultIp ?? null
          ),
          cn5gUdrIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UDR].defaultIp ?? null
          ),
          cn5gNrfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NRF].defaultIp ?? null
          ),
          cn5gNssfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NSSF].defaultIp ?? null
          ),
          cn5gNefIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NEF].defaultIp ?? null
          ),
          cn5gNwdafIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NWDAF].defaultIp ?? null
          ),
          cn5gLmfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.LMF].defaultIp ?? null
          ),
          cn5gPcfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.PCF].defaultIp ?? null
          ),
          cn5gUdsfIp: new FormControl<string | null>(
            CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UDSF].defaultIp ?? null
          ),
          cn5gOpenSpeedTestIp: new FormControl<string | null>(
            UPF_COMPONENTS_CONFIG[UpfComponents.OPEN_SPEED_TEST].defaultIp ??
              null
          ),
          cn5gOaiExtDnIp: new FormControl<string | null>(
            UPF_COMPONENTS_CONFIG[UpfComponents.OAI_EXT_DN].defaultIp ?? null
          ),
          cn5gImsIp: new FormControl<string | null>(
            UPF_COMPONENTS_CONFIG[UpfComponents.IMS].defaultIp ?? null
          ),
        },
        cn5gConfigurationValidator
      ),
    });

    // Offline mode can only be edited by root users.
    if (!this.isRoot)
      this.updateDeploymentForm.controls.offlineConfiguration.disable();

    const tenantId = activatedRoute.snapshot.paramMap.get("tenantId");
    if (tenantId) {
      this.refreshTenant(tenantId);
      this.tenantLogoUrl = this.tenantManagementService.getLogoUrl(tenantId);
    }
  }

  private refreshTenant(tenantId: TenantId) {
    this.tenantSubscription?.unsubscribe();
    this.tenantSubscription = this.tenantManagementService
      .fetchTenant(tenantId)
      .subscribe((tenant) => {
        this.tenant = tenant;

        // License Configuration
        if (tenant.license) {
          let startDate = new Date().toISOString().slice(0, 10);
          if (tenant.license.startTimestamp) {
            startDate = new Date(tenant.license.startTimestamp)
              .toISOString()
              .slice(0, 10);
          }

          let endDate = new Date().toISOString().slice(0, 10);
          if (
            tenant.license.status !== LicenseStatus.PERPETUAL &&
            tenant.license.endTimestamp
          ) {
            endDate = new Date(tenant.license.endTimestamp)
              .toISOString()
              .slice(0, 10);
          }

          this.updateDeploymentForm.controls.licenseConfiguration.patchValue({
            configurationIsShown: true,
            status: tenant.license.status,
            startDate: startDate,
            endDate: endDate,
          });
          if (tenant.license.status === LicenseStatus.PERPETUAL) {
            this.updateDeploymentForm.controls.licenseConfiguration.controls.endDate.disable();
          }
        }

        this.updateDeploymentForm.patchValue({
          name: tenant.clientName,
          description: tenant.clientDescription,

          education: tenant.education,
          kubernetes: tenant.kubernetes,
          advancedMode: tenant.advancedMode,

          flexRic: tenant.flexRic,
          ueManagement: { enabled: tenant.ueManagement?.enabled ?? false },
          networkSlicing: tenant.slicing,

          mobility: tenant.handover,
          qosium: tenant.qosium,
          ris: tenant.ris,
          multiRan: tenant.multiRan ?? false,
          redCapConfiguration: {
            redCap: tenant.redCap,
            halfDuplexRedCapAllowedR17:
              tenant.redCapConfiguration?.halfDuplexRedCapAllowedR17 ?? 0,
            cellBarredRedCap1RxR17:
              tenant.redCapConfiguration?.cellBarredRedCap1RxR17 ?? 1,
            cellBarredRedCap2RxR17:
              tenant.redCapConfiguration?.cellBarredRedCap2RxR17 ?? 1,
            intraFreqReselectionRedCapR17:
              tenant.redCapConfiguration?.intraFreqReselectionRedCapR17 ?? 0,
            t300: tenant.timersConfiguration?.t300 ?? 400,
            t301: tenant.timersConfiguration?.t301 ?? 400,
            t310: tenant.timersConfiguration?.t310 ?? 2000,
            t311: tenant.timersConfiguration?.t311 ?? 3000,
            n310: tenant.timersConfiguration?.n310 ?? 20,
            n311: tenant.timersConfiguration?.n311 ?? 1,
            t319: tenant.timersConfiguration?.t319 ?? 400,
          },

          digitalTwin: tenant.digitalTwin,
          isac: tenant.isac,
          radarDetection: tenant.radarDetection,
          visionAidedCommunications: tenant.visionAidedCommunications,

          offlineConfiguration: { enabled: tenant.offline?.enable ?? false },

          cn5gConfiguration: {
            cn5gRemote: tenant.cn5gRemote,
            cn5gIp: tenant.cn5gConfiguration?.cn5gIp,
            cn5gInterfaceName: tenant.cn5gConfiguration?.interfaceName,
            cn5gNextHop: tenant.cn5gConfiguration?.nextHop,
            cn5gAmfIp:
              tenant.cn5gConfiguration?.amfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.AMF].defaultIp ??
              null,
            cn5gUpfIp:
              tenant.cn5gConfiguration?.upfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UPF].defaultIp ??
              null,
            cn5gSmfIp:
              tenant.cn5gConfiguration?.smfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.SMF].defaultIp ??
              null,
            cn5gAusfIp:
              tenant.cn5gConfiguration?.ausfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.AUSF].defaultIp ??
              null,
            cn5gUdmIp:
              tenant.cn5gConfiguration?.udmIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UDM].defaultIp ??
              null,
            cn5gUdrIp:
              tenant.cn5gConfiguration?.udrIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UDR].defaultIp ??
              null,
            cn5gNrfIp:
              tenant.cn5gConfiguration?.nrfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NRF].defaultIp ??
              null,
            cn5gNssfIp:
              tenant.cn5gConfiguration?.nssfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NSSF].defaultIp ??
              null,
            cn5gNefIp:
              tenant.cn5gConfiguration?.nefIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NEF].defaultIp ??
              null,
            cn5gNwdafIp:
              tenant.cn5gConfiguration?.nwdafIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.NWDAF].defaultIp ??
              null,
            cn5gLmfIp:
              tenant.cn5gConfiguration?.lmfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.LMF].defaultIp ??
              null,
            cn5gPcfIp:
              tenant.cn5gConfiguration?.pcfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.PCF].defaultIp ??
              null,
            cn5gUdsfIp:
              tenant.cn5gConfiguration?.udsfIp ??
              CN5G_FUNCTION_CONFIG[AvailableCn5gFunction.UDSF].defaultIp ??
              null,
            cn5gOpenSpeedTestIp:
              tenant.cn5gConfiguration?.openSpeedTestIp ??
              UPF_COMPONENTS_CONFIG[UpfComponents.OPEN_SPEED_TEST].defaultIp ??
              null,
            cn5gOaiExtDnIp:
              tenant.cn5gConfiguration?.oaiExtDnIp ??
              UPF_COMPONENTS_CONFIG[UpfComponents.OAI_EXT_DN].defaultIp ??
              null,
            cn5gImsIp:
              tenant.cn5gConfiguration?.imsIp ??
              UPF_COMPONENTS_CONFIG[UpfComponents.IMS].defaultIp ??
              null,
          },
        });

        const oaiUeMachineIds =
          this.updateDeploymentForm.controls.ueManagement.controls
            .oaiUeMachineIds;
        oaiUeMachineIds.clear();
        (tenant.ueManagement?.oaiUeMachineIds ?? []).forEach((id) => {
          oaiUeMachineIds.push(
            new FormControl<string | null>(id, [
              Validators.required,
              Validators.pattern(UUID_PATTERN),
            ])
          );
        });
        if (tenant.ueManagement?.enabled && oaiUeMachineIds.length === 0) {
          oaiUeMachineIds.push(
            new FormControl<string | null>(null, [
              Validators.pattern(UUID_PATTERN),
            ])
          );
        }

        this.updateDeploymentForm.controls.fronthaulTypes.controls.forEach(
          (fronthaul) =>
            fronthaul.patchValue({
              checked:
                fronthaul.value.type &&
                tenant.availableFronthaulTypes.includes(fronthaul.value.type),
            })
        );
        this.updateDeploymentForm.controls.frequencyRanges.controls.forEach(
          (frequencyRange) =>
            frequencyRange.patchValue({
              checked:
                frequencyRange.value.type &&
                tenant.availableFrequencyRanges.includes(
                  frequencyRange.value.type
                ),
            })
        );
        this.updateDeploymentForm.controls.duplexModes.controls.forEach(
          (duplexModes) =>
            duplexModes.patchValue({
              checked:
                duplexModes.value.type &&
                tenant.availableDuplexModes.includes(duplexModes.value.type),
            })
        );
      });

    if (this.isRoot) {
      this.tenantFlagsSubscription?.unsubscribe();
      this.tenantFlagsSubscription = this.tenantManagementService
        .getTenantFlags(tenantId)
        .subscribe((tenantFlag) => {
          this.domainAutoAssignmentEnabled =
            tenantFlag.domainAutoAssignmentEnabled;
          this.updateDeploymentForm.patchValue({
            domainAutoAssignment: tenantFlag.domainAutoAssignmentEnabled,
          });
        });
    }
  }

  private getSelectedAvailableFronthaulTypes(): FronthaulType[] {
    const availableFronthaulType =
      this.updateDeploymentForm.controls.fronthaulTypes.controls
        .filter((fronthaul) => fronthaul.value.checked)
        .map((fronthaul) => fronthaul.value.type);
    return availableFronthaulType.length > 0
      ? (availableFronthaulType as FronthaulType[])
      : [FronthaulType.SPLIT_8];
  }

  private getSelectedAvailableFrequencyRanges(): FrequencyRange[] {
    const availableFrequencyRanges =
      this.updateDeploymentForm.controls.frequencyRanges.controls
        .filter((frequencyRange) => frequencyRange.value.checked)
        .map((frequencyRange) => frequencyRange.value.type);
    return availableFrequencyRanges.length > 0
      ? (availableFrequencyRanges as FrequencyRange[])
      : [FrequencyRange.FR1];
  }

  private getSelectedAvailableDuplexModes(): DuplexMode[] {
    const availableDuplexModes =
      this.updateDeploymentForm.controls.duplexModes.controls
        .filter((duplexMode) => duplexMode.value.checked)
        .map((duplexMode) => duplexMode.value.type);
    return availableDuplexModes.length > 0
      ? (availableDuplexModes as DuplexMode[])
      : [DuplexMode.TDD];
  }

  protected toggleOffline(): void {
    const enabled =
      this.updateDeploymentForm.controls.offlineConfiguration.controls.enabled;
    enabled.patchValue(!enabled.value);
    enabled.markAsDirty();
  }

  protected toggleUeManagement(): void {
    const ueManagement = this.updateDeploymentForm.controls.ueManagement;
    const enabled = ueManagement.controls.enabled;
    enabled.patchValue(!enabled.value);
    enabled.markAsDirty();
    if (!enabled.value) {
      ueManagement.controls.oaiUeMachineIds.clear();
    } else if (ueManagement.controls.oaiUeMachineIds.length === 0) {
      this.addOaiUeMachineId();
    }
  }

  protected addOaiUeMachineId(): void {
    this.updateDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.push(
      new FormControl<string | null>(null, [Validators.pattern(UUID_PATTERN)])
    );
  }

  protected removeOaiUeMachineId(index: number): void {
    this.updateDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.removeAt(
      index
    );
    this.updateDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.markAsDirty();
  }

  protected toggleFormControl(control: FormControl): void {
    if (!control) return;

    control.patchValue(!control.value);
    control.markAsDirty();
  }

  protected onSelectLicenseStatus(licenseStatus: LicenseStatus): void {
    const statusFormControl =
      this.updateDeploymentForm.controls.licenseConfiguration.controls.status;
    statusFormControl.patchValue(licenseStatus);
    statusFormControl.markAsDirty();

    const endDateFormControl =
      this.updateDeploymentForm.controls.licenseConfiguration.controls.endDate;
    if (licenseStatus === LicenseStatus.PERPETUAL) endDateFormControl.disable();
    else endDateFormControl.enable();
  }

  protected updateDeployment(): void {
    if (!this.tenant || this.updateDeploymentForm.invalid) return;

    const licenseConfigurationFormGroup =
      this.updateDeploymentForm.controls.licenseConfiguration.value;
    let licenseDto: LicenseDto | undefined;
    // Build LicenseDto only when License Configuration section is enabled/visible
    if (licenseConfigurationFormGroup.configurationIsShown) {
      let startTimestamp: number | undefined;
      if (licenseConfigurationFormGroup.startDate) {
        startTimestamp = new Date(
          licenseConfigurationFormGroup.startDate
        ).getTime();
      }

      let endTimestamp: number | undefined;
      if (licenseConfigurationFormGroup.endDate) {
        endTimestamp = new Date(
          licenseConfigurationFormGroup.endDate
        ).getTime();
      }

      licenseDto = {
        status: licenseConfigurationFormGroup.status ?? undefined,
        startTimestamp: startTimestamp,
        endTimestamp: endTimestamp,
      };

      // Only an edited license restarts the machines, saving other deployment fields must not
      if (this.updateDeploymentForm.controls.licenseConfiguration.dirty) {
        const actionDto: LicenseActionDto = {
          type: ActionType.LICENSE,
          command: ActionCommand.UPDATE_LICENSE,
          license: licenseDto,
        };

        const tenantId = this.tenant.id;
        this.tenant.registeredMachines.forEach((machine) => {
          this.actionsService.publishAction(tenantId, machine.id, actionDto);
        });
      }
    }

    const availableDuplexModes = this.getSelectedAvailableDuplexModes();
    const availableFronthaulType = this.getSelectedAvailableFronthaulTypes();
    const availableFrequencyRanges = this.getSelectedAvailableFrequencyRanges();
    let updateDeployment: UpdateTenantDto = {
      clientName: this.updateDeploymentForm.value.name ?? "",
      clientDescription: this.updateDeploymentForm.value.description ?? "",
      license: licenseDto,
      offline: {
        enable:
          this.updateDeploymentForm.controls.offlineConfiguration.getRawValue()
            .enabled ?? false,
      },
      availableFronthaulTypes: availableFronthaulType,
      availableFrequencyRanges: availableFrequencyRanges,
      availableDuplexModes: availableDuplexModes,

      education: this.updateDeploymentForm.value.education ?? false,
      kubernetes: this.updateDeploymentForm.value.kubernetes ?? false,
      advancedMode: this.updateDeploymentForm.value.advancedMode ?? false,
      advancedModeConfiguration: {
        enable: false,
      },

      flexRic: this.updateDeploymentForm.value.flexRic ?? false,
      slicing: this.updateDeploymentForm.value.networkSlicing ?? false,
      ueManagement: {
        enabled:
          this.updateDeploymentForm.controls.ueManagement.controls.enabled
            .value ?? false,
        oaiUeMachineIds:
          this.updateDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds.controls
            .map((c) => c.value)
            .filter((v): v is string => !!v),
      },

      handover: this.updateDeploymentForm.value.mobility ?? false,
      qosium: this.updateDeploymentForm.value.qosium ?? false,
      ris: this.updateDeploymentForm.value.ris ?? false,
      multiRan: this.updateDeploymentForm.value.multiRan ?? false,
      redCap:
        this.updateDeploymentForm.controls.redCapConfiguration.value.redCap ??
        false,

      digitalTwin: this.updateDeploymentForm.value.digitalTwin ?? false,
      isac: this.updateDeploymentForm.value.isac ?? false,
      radarDetection: this.updateDeploymentForm.value.radarDetection ?? false,
      visionAidedCommunications:
        this.updateDeploymentForm.value.visionAidedCommunications ?? false,

      cn5gRemote:
        this.updateDeploymentForm.controls.cn5gConfiguration.value.cn5gRemote ??
        false,
    };

    if (
      updateDeployment.advancedMode &&
      this.tenant.advancedModeConfiguration?.enable !== undefined
    ) {
      updateDeployment.advancedModeConfiguration = {
        enable: this.tenant.advancedModeConfiguration.enable,
      };
    }

    if (updateDeployment.cn5gRemote) {
      updateDeployment.cn5gConfiguration = {
        cn5gIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value.cn5gIp!,
        interfaceName:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gInterfaceName!,
        nextHop:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gNextHop ?? undefined,
        amfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value.cn5gAmfIp!,
        upfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gUpfIp ?? undefined,
        smfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gSmfIp ?? undefined,
        ausfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gAusfIp ?? undefined,
        udmIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gUdmIp ?? undefined,
        udrIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gUdrIp ?? undefined,
        nrfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gNrfIp ?? undefined,
        nssfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gNssfIp ?? undefined,
        nefIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gNefIp ?? undefined,
        nwdafIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gNwdafIp ?? undefined,
        lmfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gLmfIp ?? undefined,
        pcfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gPcfIp ?? undefined,
        udsfIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gUdsfIp ?? undefined,
        openSpeedTestIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gOpenSpeedTestIp ?? undefined,
        oaiExtDnIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gOaiExtDnIp ?? undefined,
        imsIp:
          this.updateDeploymentForm.controls.cn5gConfiguration.value
            .cn5gImsIp ?? undefined,
      };
    }

    if (updateDeployment.redCap) {
      updateDeployment.redCapConfiguration = {
        halfDuplexRedCapAllowedR17:
          this.updateDeploymentForm.controls.redCapConfiguration.value
            .halfDuplexRedCapAllowedR17 ?? 0,
        cellBarredRedCap1RxR17:
          this.updateDeploymentForm.controls.redCapConfiguration.value
            .cellBarredRedCap1RxR17 ?? 1,
        cellBarredRedCap2RxR17:
          this.updateDeploymentForm.controls.redCapConfiguration.value
            .cellBarredRedCap2RxR17 ?? 1,
        intraFreqReselectionRedCapR17:
          this.updateDeploymentForm.controls.redCapConfiguration.value
            .intraFreqReselectionRedCapR17 ?? 0,
      };

      updateDeployment.timersConfiguration = {
        t300:
          this.updateDeploymentForm.controls.redCapConfiguration.value.t300 ??
          400,
        t301:
          this.updateDeploymentForm.controls.redCapConfiguration.value.t301 ??
          400,
        t310:
          this.updateDeploymentForm.controls.redCapConfiguration.value.t310 ??
          2000,
        t311:
          this.updateDeploymentForm.controls.redCapConfiguration.value.t311 ??
          3000,
        n310:
          this.updateDeploymentForm.controls.redCapConfiguration.value.n310 ??
          20,
        n311:
          this.updateDeploymentForm.controls.redCapConfiguration.value.n311 ??
          1,
        t319:
          this.updateDeploymentForm.controls.redCapConfiguration.value.t319 ??
          400,
      };
    }

    if (updateDeployment.redCapConfiguration === undefined)
      updateDeployment.redCapConfiguration = {
        enable: updateDeployment.redCap,
      };
    else updateDeployment.redCapConfiguration.enable = updateDeployment.redCap;

    this.ongoingRequest = true;
    this.updateDeploymentSubscription?.unsubscribe();
    this.updateDeploymentSubscription = this.tenantManagementService
      .updateTenant(this.tenant.id, updateDeployment)
      .pipe(
        switchMap((client) => {
          if (this.addTenantLogoFile) {
            return this.tenantManagementService
              .createOrUpdateTenantLogo(client.id, this.addTenantLogoFile)
              .pipe(map(() => client));
          } else if (this.removeTenantLogoFile) {
            return this.tenantManagementService
              .deleteTenantLogo(client.id)
              .pipe(
                tap(() => (this.removeTenantLogoFile = false)),
                map(() => client)
              );
          } else {
            return of(client);
          }
        }),
        switchMap((client) => {
          if (
            this.isRoot &&
            this.domainAutoAssignmentEnabled !=
              this.updateDeploymentForm.value.domainAutoAssignment &&
            this.updateDeploymentForm.value.domainAutoAssignment != null
          ) {
            this.domainAutoAssignmentEnabled =
              this.updateDeploymentForm.value.domainAutoAssignment;
            let updateDeploymentDto: DeploymentFlagsDto = {
              domainAutoAssignmentEnabled:
                this.updateDeploymentForm.value.domainAutoAssignment,
            };
            return this.tenantManagementService
              .updateTenantFlags(client.id, updateDeploymentDto)
              .pipe(map(() => client));
          } else {
            return of(client);
          }
        })
      )
      .subscribe({
        next: (updatedTenant) => {
          this.tenant = updatedTenant;
          this.ongoingRequest = false;
          this.tenantManagementService.refreshTenants();
          this.simpleMessageService.showSimpleMessage({
            message: "Deployment updated successfully!",
            icon: "fa-solid fa-check-double",
            color: "success",
          });
          this.redirectToDeploymentsPage();
        },
        error: () => {
          this.ongoingRequest = false;
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to update deployment!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  }

  protected redirectToDeploymentsPage(): void {
    this.router.navigate([AvailableRoutes.ADMIN, AvailableRoutes.DEPLOYMENTS]);
  }

  protected addTenantLogo(logoFile: File): void {
    this.addTenantLogoFile = logoFile;
    this.removeTenantLogoFile = false;
  }

  protected removeTenantLogo(): void {
    this.removeTenantLogoFile = true;
    this.addTenantLogoFile = undefined;
  }

  protected openDeploymentDomainUpdateModal() {
    this.refreshTenantSubscription?.unsubscribe();
    this.refreshTenantSubscription = this.modalService
      .modalClose$()
      .pipe(first())
      .subscribe(() => {
        if (this.tenant) this.refreshTenant(this.tenant.id);
      });

    this.modalService.pushModal({
      component: UpdateDeploymentDomainModalComponent,
      inputs: new Map<string, any>([["tenant", this.tenant]]),
      size: "medium",
      responsiveWidth: true,
    });
  }

  ngOnDestroy(): void {
    this.updateDeploymentSubscription?.unsubscribe();
    this.tenantSubscription?.unsubscribe();
    this.refreshTenantSubscription?.unsubscribe();
    this.tenantFlagsSubscription?.unsubscribe();
  }
}
